import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Building2, Clock, CheckCircle, XCircle, 
  Calendar, Video, Phone, Users, TrendingUp, Award, IndianRupee,
  FileText, ExternalLink, AlertCircle, Star, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlacementDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view placements');
      navigate('/auth/login');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [appsRes, interviewsRes, placementsRes, recsRes, profileRes] = await Promise.all([
        axios.get(`${API}/student/applications/status`),
        axios.get(`${API}/student/interviews`),
        axios.get(`${API}/student/placements`),
        axios.get(`${API}/jobs/ai-recommendations`).catch(() => ({ data: { recommendations: [] } })),
        axios.get(`${API}/profile/student`).catch(() => ({ data: null }))
      ]);
      setApplications(appsRes.data || []);
      setInterviews(interviewsRes.data || []);
      setPlacements(placementsRes.data || []);
      setRecommendations(recsRes.data.recommendations || []);
      setProfileData(profileRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = () => {
    if (!profileData?.is_complete) {
      toast.error('Pehle apni Profile complete karein, fir job apply kar sakte hain', {
        description: 'Profile update page par redirect kiya jaa raha hai...',
        duration: 4000
      });
      setTimeout(() => navigate('/student/profile'), 800);
      return;
    }
    navigate('/jobs');
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-amber-100 text-amber-700',
      'shortlisted': 'bg-blue-100 text-blue-700',
      'interview_scheduled': 'bg-purple-100 text-purple-700',
      'hired': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'scheduled': 'bg-purple-100 text-purple-700',
      'completed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-slate-100 text-slate-700',
      'confirmed': 'bg-green-100 text-green-700',
      'joined': 'bg-emerald-100 text-emerald-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getInterviewTypeIcon = (type) => {
    switch (type) {
      case 'Video Call': return <Video className="h-4 w-4" />;
      case 'Phone Call': return <Phone className="h-4 w-4" />;
      default: return <Building2 className="h-4 w-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats
  const stats = {
    total_applications: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    interviews: applications.filter(a => a.status === 'interview_scheduled').length,
    hired: applications.filter(a => a.status === 'hired').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  const upcomingInterviews = interviews.filter(i => i.status === 'scheduled');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="h-7 w-7 text-blue-600" />
            Placement Dashboard
          </h1>
          <p className="text-slate-600">Track your job applications, interviews, and placements</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
            <p className="text-2xl font-bold text-slate-900">{stats.total_applications}</p>
            <p className="text-sm text-slate-600">Total Applied</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-amber-500">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-sm text-slate-600">Pending</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-indigo-500">
            <p className="text-2xl font-bold text-indigo-600">{stats.shortlisted}</p>
            <p className="text-sm text-slate-600">Shortlisted</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
            <p className="text-2xl font-bold text-purple-600">{stats.interviews}</p>
            <p className="text-sm text-slate-600">Interviews</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
            <p className="text-2xl font-bold text-green-600">{stats.hired}</p>
            <p className="text-sm text-slate-600">Offers</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            <p className="text-sm text-slate-600">Rejected</p>
          </div>
        </div>

        {/* Upcoming Interviews Alert */}
        {upcomingInterviews.length > 0 && (
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-6 w-6" />
              <h3 className="font-bold text-lg">Upcoming Interviews ({upcomingInterviews.length})</h3>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {upcomingInterviews.slice(0, 2).map((interview) => (
                <div key={interview.id} className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {getInterviewTypeIcon(interview.interview_type)}
                    <span className="font-semibold">{interview.position_name}</span>
                  </div>
                  <p className="text-sm opacity-90">{interview.company_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(interview.interview_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {interview.interview_time}
                    </span>
                  </div>
                  {interview.interview_location && (
                    <p className="text-xs mt-2 opacity-75">
                      {interview.interview_type === 'Video Call' ? 'Link: ' : 'Location: '}
                      {interview.interview_location}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-xl grid-cols-4 mb-6">
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="interviews">Interviews</TabsTrigger>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="suggestions">For You</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white rounded-xl shadow-sm">
              {applications.length === 0 ? (
                <div className="p-12 text-center">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-600">No applications yet</p>
                  <Button onClick={() => navigate('/jobs')} className="mt-4 bg-blue-600 hover:bg-blue-700">
                    Browse Jobs
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {applications.map((app) => (
                    <div key={app.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`application-${app.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900">{app.job_title || 'Job Position'}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(app.status)}`}>
                              {app.status?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 flex items-center gap-1">
                            <Building2 className="h-4 w-4" /> {app.company_name || 'Company'}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {app.location || 'Location'}
                            </span>
                            {app.ctc && (
                              <span className="flex items-center gap-1 text-green-600">
                                <IndianRupee className="h-3 w-3" /> {app.ctc} LPA
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> Applied {formatDate(app.applied_at)}
                            </span>
                          </div>
                          
                          {/* Interview Info */}
                          {app.interview && (
                            <div className="mt-3 bg-purple-50 rounded-lg p-3">
                              <p className="text-sm font-medium text-purple-700 flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Interview Scheduled - Round {app.interview.round}
                              </p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-purple-600">
                                <span>{formatDate(app.interview.date)} at {app.interview.time}</span>
                                <span className="flex items-center gap-1">
                                  {getInterviewTypeIcon(app.interview.type)}
                                  {app.interview.type}
                                </span>
                              </div>
                              {app.interview.location && (
                                <p className="text-xs text-purple-500 mt-1">{app.interview.location}</p>
                              )}
                            </div>
                          )}
                          
                          {/* Placement Info */}
                          {app.placement && (
                            <div className="mt-3 bg-green-50 rounded-lg p-3">
                              <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                                <Award className="h-4 w-4" />
                                Offer Received!
                              </p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-green-600">
                                <span>{app.placement.designation}</span>
                                <span className="flex items-center gap-1">
                                  <IndianRupee className="h-3 w-3" /> {app.placement.offered_ctc} LPA
                                </span>
                                <span>Joining: {formatDate(app.placement.joining_date)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <div className="bg-white rounded-xl shadow-sm">
              {interviews.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-600">No interviews scheduled yet</p>
                  <p className="text-sm text-slate-500 mt-1">Keep applying! You'll hear back soon.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {interviews.map((interview) => (
                    <div key={interview.id} className="p-4 hover:bg-slate-50" data-testid={`interview-${interview.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900">{interview.position_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(interview.status)}`}>
                              {interview.status?.toUpperCase()}
                            </span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              Round {interview.interview_round}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{interview.company_name}</p>
                          
                          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Date</p>
                              <p className="font-medium">{formatDate(interview.interview_date)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Time</p>
                              <p className="font-medium">{interview.interview_time}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Type</p>
                              <p className="font-medium flex items-center gap-1">
                                {getInterviewTypeIcon(interview.interview_type)}
                                {interview.interview_type}
                              </p>
                            </div>
                            {interview.interviewer_name && (
                              <div>
                                <p className="text-slate-500">Interviewer</p>
                                <p className="font-medium">{interview.interviewer_name}</p>
                              </div>
                            )}
                          </div>
                          
                          {interview.interview_location && (
                            <div className="mt-3 bg-slate-50 rounded-lg p-3">
                              <p className="text-sm text-slate-600">
                                {interview.interview_type === 'Video Call' ? (
                                  <a href={interview.interview_location} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                    <ExternalLink className="h-4 w-4" /> Join Meeting
                                  </a>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-4 w-4" /> {interview.interview_location}
                                  </span>
                                )}
                              </p>
                            </div>
                          )}
                          
                          {interview.notes && (
                            <p className="mt-2 text-sm text-slate-500 italic">Note: {interview.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <div className="bg-white rounded-xl shadow-sm">
              {placements.length === 0 ? (
                <div className="p-12 text-center">
                  <Award className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-600">No placement offers yet</p>
                  <p className="text-sm text-slate-500 mt-1">Keep preparing and giving your best in interviews!</p>
                </div>
              ) : (
                <div className="divide-y">
                  {placements.map((placement) => (
                    <div key={placement.id} className="p-6" data-testid={`placement-${placement.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Award className="h-6 w-6 text-green-600" />
                            <h3 className="font-bold text-xl text-slate-900">{placement.designation}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(placement.status)}`}>
                              {placement.status?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-lg text-slate-700">{placement.company_name}</p>
                          <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                            <MapPin className="h-4 w-4" /> {placement.location}
                          </p>
                          
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="bg-green-50 rounded-lg p-4 text-center">
                              <p className="text-sm text-green-600">Offered CTC</p>
                              <p className="text-2xl font-bold text-green-700 flex items-center justify-center gap-1">
                                <IndianRupee className="h-5 w-5" />
                                {placement.offered_ctc} LPA
                              </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4 text-center">
                              <p className="text-sm text-blue-600">Position</p>
                              <p className="font-semibold text-blue-700">{placement.position_name}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4 text-center">
                              <p className="text-sm text-purple-600">Joining Date</p>
                              <p className="font-semibold text-purple-700">{formatDate(placement.joining_date)}</p>
                            </div>
                            {placement.department && (
                              <div className="bg-slate-50 rounded-lg p-4 text-center">
                                <p className="text-sm text-slate-600">Department</p>
                                <p className="font-semibold text-slate-700">{placement.department}</p>
                              </div>
                            )}
                          </div>
                          
                          {placement.offer_letter_url && (
                            <a 
                              href={placement.offer_letter_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:text-blue-700"
                            >
                              <FileText className="h-4 w-4" />
                              View Offer Letter
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Job Suggestions Tab */}
          <TabsContent value="suggestions">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-lg text-slate-900">AI-Powered Job Suggestions</h3>
              </div>
              
              {recommendations.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-600">Complete your profile to get personalized job recommendations</p>
                  <Button onClick={() => navigate('/student/profile')} className="mt-4" variant="outline">
                    Update Profile
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {recommendations.map((job, idx) => (
                    <div key={job.id || idx} className="border border-purple-200 rounded-xl p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-purple-50">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                          <Star className="h-3 w-3" /> {job.match_score}% Match
                        </span>
                      </div>
                      
                      <h4 className="font-semibold text-slate-900">{job.position_name}</h4>
                      <p className="text-sm text-slate-600">{job.company_name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" /> {job.location}
                      </p>
                      
                      {job.reasons && job.reasons.length > 0 && (
                        <div className="mt-3 bg-purple-50 rounded-lg p-2">
                          <p className="text-xs font-medium text-purple-700 mb-1">Why this job?</p>
                          <ul className="text-xs text-purple-600 space-y-0.5">
                            {job.reasons.slice(0, 2).map((reason, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-green-600 font-semibold flex items-center gap-1">
                          <IndianRupee className="h-4 w-4" />
                          {job.min_ctc} LPA
                        </span>
                        <Button size="sm" onClick={handleApplyClick} className="bg-purple-600 hover:bg-purple-700">
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 text-center">
                <Button onClick={() => navigate('/jobs')} variant="outline">
                  View All Jobs
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default PlacementDashboard;
